import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ModalWrapper } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { Tag } from "@msuite/ui/tag";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import { comparableReference } from "@msuite/utils/incoming-invoices";
import type {
	IncomingInvoiceDeliveryNoteItem,
	IncomingInvoiceDetail,
	Nullable,
} from "@msuite/utils/types";
import React from "react";
import { FileCardRemote } from "@/components/files";
import { formatInvoiceDate } from "@/components/incoming-invoices";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { LinkDeliveryNoteModal } from "@/modals/incoming-invoices/link-delivery-note-modal";

/** Props Interface */
interface IncomingInvoiceDeliveryNotesCardProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/**
 * The delivery notes the invoice bills, shown as the documents they are — like the invoice's own
 * PDF beneath — and every number the invoice cites that no delivery note answered yet, so a
 * missing one shows before anybody looks for it. A caller with `update IncomingInvoice` links a
 * waiting delivery note by hand and takes a link back, which leaves the delivery note waiting
 * again; its PDF is its email's attachment, so nothing here deletes it. A duplicate takes none.
 */
export function IncomingInvoiceDeliveryNotesCard({
	incomingInvoice,
}: IncomingInvoiceDeliveryNotesCardProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const qc = useQueryClient();
	const linkModal = useDisclosure();

	/** Functions */
	async function handleOnUnlink(deliveryNoteId: string) {
		const response = await client.incoming_invoices["delivery-notes"][":deliveryNoteId"].$patch({
			param: { deliveryNoteId },
			json: { incomingInvoiceId: null },
		});
		await responseHandler(response);
	}

	async function handleOnUnlinked() {
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
	}

	function captionOf(deliveryNote: IncomingInvoiceDeliveryNoteItem) {
		const parts = [
			formatInvoiceDate(deliveryNote.deliveryDate),
			t("incoming_invoices.delivery_notes.position_count", {
				count: String(deliveryNote.positionCount),
			}),
		];
		if (deliveryNote.orderReference) {
			parts.push(
				`${t("incoming_invoices.delivery_notes.order_reference")} ${deliveryNote.orderReference}`,
			);
		}
		return parts.join(" · ");
	}

	/** Computed */
	const deliveryNotes = incomingInvoice?.deliveryNotes ?? [];
	const received = new Set(
		deliveryNotes.map((deliveryNote) => comparableReference(deliveryNote.number)),
	);
	const notReceived = (incomingInvoice?.deliveryNoteNumbers ?? []).filter(
		(number) => !received.has(comparableReference(number)),
	);
	const isLinkable =
		!!incomingInvoice &&
		incomingInvoice.state !== "duplicate" &&
		incomingInvoice.state !== "merged";
	const mayLink = isLinkable && citadel?.can("update", "IncomingInvoice") === true;
	const isEmpty = !deliveryNotes.length && !notReceived.length;

	/** Guard */
	if (!incomingInvoice) return null;

	/** Render */
	return (
		<React.Fragment>
			<ContentCard
				title={t("incoming_invoices.delivery_notes.delivery_notes")}
				icon={icon("Truck")}
				variant="plain"
				accessory={
					mayLink && (
						<Tooltip label={t("incoming_invoices.delivery_notes.link_delivery_note")}>
							<Button
								size="xs"
								variant="plain"
								icon={<Icon name="Link2" />}
								onClick={linkModal.onOpen}
								data-cy="link-delivery-note-to-incoming-invoice-button"
							/>
						</Tooltip>
					)
				}
			>
				{isEmpty ? (
					<BlankSlate
						label={t("incoming_invoices.delivery_notes.none")}
						Icon={icon("Truck")}
						onClick={mayLink ? linkModal.onOpen : undefined}
					/>
				) : (
					<React.Fragment>
						{deliveryNotes.map((deliveryNote) =>
							deliveryNote.fileId ? (
								<FileCardRemote
									key={deliveryNote.id}
									fileId={deliveryNote.fileId}
									title={t("incoming_invoices.delivery_notes.title", {
										number: deliveryNote.number ?? "–",
									})}
									caption={captionOf(deliveryNote)}
									onInteraction={handleOnUnlinked}
									onUnlink={mayLink ? () => handleOnUnlink(deliveryNote.id) : undefined}
									withRemove={mayLink}
									canRemove={mayLink}
									withDelete={false}
									variant="content-card"
								/>
							) : null,
						)}
						{notReceived.length > 0 && (
							<div className="h-stack gap-2 flex-wrap px-1">
								<span className="label">{t("incoming_invoices.delivery_notes.not_received")}</span>
								{notReceived.map((number) => (
									<Tag
										key={number}
										size="xs"
										color="amber"
										variant="plain"
									>
										{number}
									</Tag>
								))}
							</div>
						)}
					</React.Fragment>
				)}
			</ContentCard>
			<ModalWrapper>
				{linkModal.isOpen && (
					<LinkDeliveryNoteModal
						isOpen={linkModal.isOpen}
						onClose={linkModal.onClose}
						incomingInvoiceId={incomingInvoice.id}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
